import { useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { SignIn } from "@phosphor-icons/react";
import api, { formatApiError, LOGO_URL } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const BG_URL = "/login-bg.jpg";

export default function Login() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user && user !== false) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data);
      navigate(data.must_reset_password ? "/change-password" : "/");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen">
      <div
        className="relative hidden flex-1 bg-cover bg-center lg:block"
        style={{ backgroundImage: `url(${BG_URL})` }}
      >
        <div className="absolute inset-0 bg-slate-900/60" />
        <div className="absolute bottom-12 left-12 max-w-md text-white">
          <h2 className="text-3xl font-bold">Track every order, from entry to delivery.</h2>
          <p className="mt-3 text-sm text-slate-200">
            Sales, factory and accounts — one live view of production, dispatch and aging.
          </p>
        </div>
      </div>
      <div className="flex w-full items-center justify-center bg-white px-6 lg:w-[480px] lg:shrink-0">
        <div className="w-full max-w-sm fade-in-up">
          <img src={LOGO_URL} alt="DCN Food and Beverages" className="h-20 w-auto" />
          <h1 className="mt-6 text-2xl font-bold text-slate-900">DCN Order and Dispatch Hub</h1>
          <p className="mt-1 text-sm text-slate-500">Sign in with your team account</p>
          <form onSubmit={submit} className="mt-8 space-y-4">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Email or Username</label>
              <input
                data-testid="login-email-input"
                type="text"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@dcn.com or username"
                className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none transition-colors duration-150 focus:border-slate-900"
              />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Password</label>
              <input
                data-testid="login-password-input"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none transition-colors duration-150 focus:border-slate-900"
              />
            </div>
            {error && (
              <p data-testid="login-error" className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                {error}
              </p>
            )}
            <button
              data-testid="login-submit-button"
              type="submit"
              disabled={loading}
              className="flex w-full items-center justify-center gap-2 rounded-md bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white transition-colors duration-150 hover:bg-slate-700 disabled:opacity-60"
            >
              <SignIn size={16} weight="bold" />
              {loading ? "Signing in…" : "Sign in"}
            </button>
            <Link to="/forgot-password" data-testid="forgot-password-link"
              className="block text-center text-sm text-slate-500 underline-offset-4 hover:underline">
              Forgot password?
            </Link>
          </form>
          <div className="mt-8 rounded-md border border-slate-200 bg-slate-50 p-4 text-xs text-slate-600">
            <p className="font-semibold uppercase tracking-wider text-slate-500">Need an account?</p>
            <p className="mt-1">
              Team members: ask your admin to create one from the Users page.{" "}
              Customers:{" "}
              <Link to="/register" data-testid="register-link" className="font-semibold text-slate-900 underline underline-offset-4 hover:text-slate-600">
                register here
              </Link>{" "}
              to order directly.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
